<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';

import { eqsApi } from '@/api/eqs';
import AppDrawer from '@/components/AppDrawer.vue';
import { notify } from '@/services/notice';
import { errorMessage } from '@/utils/format';

interface ChangeOption {
  id: number;
  code: string | null;
  title: string;
  status: string;
  unassigned: boolean;
  itemIds: number[];
}

interface PartRow {
  id: number;
  level: number;
  partNumber: string;
  partName: string;
}

const props = defineProps<{ projectId: string | number; parts: PartRow[] }>();
const emit = defineEmits<{ (event: 'close'): void; (event: 'saved', payload: unknown): void }>();

const loading = ref(true);
const saving = ref(false);
const error = ref('');
const changes = ref<ChangeOption[]>([]);
const selectedId = ref<number | null>(null);
const picked = ref(new Set<number>());
const query = ref('');

const selected = computed(() => changes.value.find((item) => item.id === selectedId.value) ?? null);
const visibleParts = computed(() => {
  const keyword = query.value.trim().toLowerCase();
  if (!keyword) return props.parts;
  return props.parts.filter((part) =>
    `${part.partNumber} ${part.partName}`.toLowerCase().includes(keyword));
});

async function load(): Promise<void> {
  loading.value = true;
  error.value = '';
  try {
    const payload = await eqsApi.bomChanges(props.projectId) as unknown as { changes: ChangeOption[] };
    changes.value = payload.changes;
    selectedId.value = payload.changes[0]?.id ?? null;
  } catch (caught) {
    error.value = errorMessage(caught);
  } finally {
    loading.value = false;
  }
}

watch(selected, (value) => {
  picked.value = new Set(value?.itemIds ?? []);
});

function toggle(part: PartRow): void {
  const next = new Set(picked.value);
  if (next.has(part.id)) next.delete(part.id);
  else next.add(part.id);
  picked.value = next;
}

async function save(): Promise<void> {
  if (!selected.value) return;
  saving.value = true;
  try {
    const result = await eqsApi.setBomChangeParts(
      props.projectId, selected.value.id, [...picked.value],
    );
    notify('success', '已保存', `「${selected.value.title}」影响 ${picked.value.size} 个零件。`);
    emit('saved', result);
    emit('close');
  } catch (caught) {
    notify('error', '保存失败', errorMessage(caught));
  } finally {
    saving.value = false;
  }
}

onMounted(load);
</script>

<template>
  <AppDrawer title="变更单影响的零件" @close="emit('close')">
    <template #subtitle>
      <p>勾上这张变更单要改的零件，BOM 表里会标出「变更中」。</p>
    </template>

      <p v-if="loading" class="drawer-note">正在读取变更单…</p>
      <p v-else-if="error" class="drawer-note is-error">{{ error }}</p>
      <p v-else-if="!changes.length" class="drawer-note">
        还没有变更单。在「变更」页建一张之后再回来关联零件。
      </p>

      <template v-else>
        <label class="drawer-field">
          <span>变更单</span>
          <select v-model="selectedId">
            <option v-for="option in changes" :key="option.id" :value="option.id">
              {{ option.code ? `${option.code} · ` : '' }}{{ option.title }}（{{ option.status }}）
            </option>
          </select>
        </label>
        <p v-if="selected?.unassigned" class="drawer-note">
          这张变更单还没归属项目，保存后会归到本项目。
        </p>

        <label class="drawer-field">
          <span>找零件</span>
          <input v-model="query" type="search" placeholder="零件号或名称">
        </label>

        <ul class="part-list">
          <li v-for="part in visibleParts" :key="part.id">
            <label :style="{ paddingLeft: `${(part.level - 1) * 16}px` }">
              <input type="checkbox" :checked="picked.has(part.id)" @change="toggle(part)">
              <b>{{ part.partNumber }}</b><span>{{ part.partName }}</span>
            </label>
          </li>
        </ul>
      </template>

    <template #footer>
      <span class="picked-count">已选 {{ picked.size }} 个零件</span>
      <button type="button" class="inline-button" @click="emit('close')">取消</button>
      <button
        type="button" class="button button-primary"
        :disabled="saving || !selected" @click="save"
      >{{ saving ? '保存中…' : '保存' }}</button>
    </template>
  </AppDrawer>
</template>

<style scoped>
.drawer-note { margin: 10px 22px 0; color: var(--muted); font-size: 12px; line-height: 1.8; }
.drawer-note.is-error { color: var(--risk); }

.drawer-field { display: grid; gap: 5px; margin: 14px 22px 0; font-size: 12px; color: var(--muted); }
.drawer-field select, .drawer-field input { min-height: 40px; padding: 0 10px; border: 1px solid var(--line); background: var(--white); color: var(--ink); font: inherit; font-size: 13px; }

.part-list { flex: 1; overflow-y: auto; margin: 12px 0 0; padding: 0 22px 16px; list-style: none; }
.part-list li { border-bottom: 1px solid var(--line); }
.part-list label { display: flex; align-items: center; gap: 8px; padding: 8px 0; cursor: pointer; }
.part-list b { font-size: 13px; }
.part-list span { color: var(--muted); font-size: 12px; }

.picked-count { margin-right: auto; color: var(--muted); font-size: 12px; }
</style>
